import { CheckCircle2, Circle } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { InfoHint } from '../InfoHint';
import type { RepairCompilerPlan } from '../../lib/relationship-intelligence-engine';

type MissionState = {
  completedStepIds: string[];
  updatedAt: number;
};

export function RepairMissionTimelineCard({
  plan,
  mission,
  onToggleStep,
  onReset,
  onTrack,
}: {
  plan: RepairCompilerPlan;
  mission: MissionState;
  onToggleStep: (stepId: string) => void;
  onReset: () => void;
  onTrack?: (event: string, properties?: Record<string, unknown>) => void;
}) {
  const completedCount = plan.steps.filter((step) => mission.completedStepIds.includes(step.id)).length;
  const progressPct = Math.round((completedCount / Math.max(plan.steps.length, 1)) * 100);

  return (
    <Card className="p-4 border-fuchsia-200/80 bg-fuchsia-50/70 dark:border-fuchsia-900/50 dark:bg-fuchsia-950/20">
      <p className="text-xs font-semibold uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300">
        <span className="inline-flex items-center gap-1.5">
          Repair mission timeline
          <InfoHint
            title="Repair mission timeline"
            description="Execute your plan as a guided mission and track completion in-session to reinforce consistency."
          />
        </span>
      </p>
      <p className="mt-1 text-sm text-neutral-800 dark:text-neutral-100">
        {plan.dayTheme} · expected score shift {plan.expectedTotalDelta.toFixed(1)} points
      </p>
      <div className="mt-2 h-2 rounded-full bg-fuchsia-200/70 dark:bg-fuchsia-900/50">
        <div className="h-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-500 transition-all" style={{ width: `${progressPct}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-neutral-600 dark:text-neutral-300">
        Mission progress: {completedCount}/{plan.steps.length} complete ({progressPct}%)
      </p>
      <ul className="mt-2 space-y-1.5">
        {plan.steps.map((step) => {
          const done = mission.completedStepIds.includes(step.id);
          return (
            <li key={step.id} className="rounded-lg border border-fuchsia-200/60 bg-white/80 px-2.5 py-1.5 text-xs dark:border-fuchsia-900/40 dark:bg-neutral-900/70">
              <button
                type="button"
                className="flex w-full items-start gap-2 text-left"
                onClick={() => {
                  onToggleStep(step.id);
                  onTrack?.('ri_mission_step_toggled', { stepId: step.id, done: !done });
                }}
              >
                {done ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" /> : <Circle className="mt-0.5 h-4 w-4 text-neutral-400" />}
                <div>
                  <p className="font-semibold text-neutral-900 dark:text-neutral-100">{step.title}</p>
                  <p className="text-neutral-600 dark:text-neutral-300">
                    {step.timing} · {step.why} · impact {step.expectedDelta.toFixed(1)}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            onReset();
            onTrack?.('ri_mission_reset');
          }}
        >
          Reset mission
        </Button>
      </div>
    </Card>
  );
}
